import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { Venue, actions as venuesActions, selectors as venuesSelectors } from 'store/venues';
import { ActionOverlay } from 'components/ActionOverlay';
import { getSortedList } from 'utils/stringFormat';
import { VenueForm } from './VenueForm';
import { useKioskFormStyles } from '../styled';
import { VenueFormData, VenueUpdateFormData } from '../../../types';

interface Props {
  onClose: () => void;
}

export const KioskVenueOverlay: React.FC<Props> = ({ onClose }) => {
  const classes = useKioskFormStyles();

  const [getVenues, deleteVenue, createVenue, updateVenue, resetError] = useActions([
    venuesActions.getVenues,
    venuesActions.deleteVenue,
    venuesActions.createVenue,
    venuesActions.updateVenue,
    venuesActions.resetError
  ]);

  const venues = useSelector(venuesSelectors.selectVenues);
  const { error, pending } = useSelector(venuesSelectors.selectAction);

  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [showCreateEdit, setShowCreateEdit] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [sortColumn, setSortColumn] = useState<'name' | 'address'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);

  const sortedVenues = useMemo(() => {
    if (!venues?.length) return [];

    const sorted = getSortedList(venues, sortColumn);
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [venues, sortColumn, sortOrder]);

  useEffect(() => {
    getVenues();
  }, [getVenues]);

  useEffect(() => {
    if (uiLoading && !pending) {
      if (!error) {
        setShowCreateEdit(false);
        setSelectedVenue(null);
        getVenues();
      }
      setUILoading(false);
    }
  }, [uiLoading, pending, error, getVenues]);

  const handleSubmit = (data: VenueFormData) => {
    setUILoading(true);

    if (selectedVenue) {
      updateVenue({
        id: selectedVenue.id,
        ...data
      });
    } else {
      createVenue(data);
    }
  };

  /**
   * EDIT VENUE
   */
  const handleEdit = (venue: Venue) => {
    setSelectedVenue(venue);
    setShowCreateEdit(true);
  };

  /**
   * DELETE VENUE
   */
  const handleDelete = () => {
    if (!selectedVenue) return;

    setUILoading(true);
    deleteVenue({ id: selectedVenue.id });

    setSelectedVenue(null);
    setShowDeleteOverlay(false);
  };

  /**
   * FORM CLOSE
   */
  const handleOnFormClose = () => {
    setShowCreateEdit(false);
    setSelectedVenue(null);

    getVenues();
  };

  /**
   * INITIAL DATA FOR EDIT
   */
  const getInitialData = (): VenueUpdateFormData | undefined => {
    if (!selectedVenue) return undefined;

    return {
      id: selectedVenue.id,
      name: selectedVenue.name,
      address: selectedVenue.address
    };
  };

  const handleSort = (column: 'name' | 'address') => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const SortArrow = ({ active }: { active: boolean }) =>
    active ? (
      <SvgIcon name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
    ) : null;

  return (
    <>
      <FormDialog
        open
        title="Venues"
        confirmButtonText="Close"
        confirmButtonAction={onClose}
        headerButtonAction={() => {
          setSelectedVenue(null);
          setShowCreateEdit(true);
        }}
        headerButtonText="Create Venue"
      >
        <MuiBox mb="1rem" mt="14px">
          <MuiBox>
            <Card hasBorderRadius raised fullHeight>
              <Box flexOverride="1">
                <MuiTable.TableContainer id="kioskVenueTable">
                  <MuiTable.Table stickyHeader>
                    <MuiTable.TableHead>
                      <MuiTable.TableRow>
                        <MuiTable.TableCell
                          className={classes.kioskDetailsHeaders}
                          sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                          onClick={() => handleSort('name')}
                        >
                          Name <SortArrow active={sortColumn === 'name'} />
                        </MuiTable.TableCell>

                        <MuiTable.TableCell
                          className={classes.kioskDetailsHeaders}
                          sx={{ width: '220px', whiteSpace: 'nowrap' }}
                          onClick={() => handleSort('address')}
                        >
                          Address <SortArrow active={sortColumn === 'address'} />
                        </MuiTable.TableCell>

                        <MuiTable.TableCell
                          className={classes.kioskDetailsHeaders}
                          sx={{ cursor: 'default', textAlign: 'center' }}
                        >
                          Actions
                        </MuiTable.TableCell>
                      </MuiTable.TableRow>
                    </MuiTable.TableHead>

                    <MuiTable.TableBody>
                      {sortedVenues.map((venue: Venue) => (
                        <MuiTable.TableRow key={venue.id}>
                          <MuiTable.TableCell>{venue.name}</MuiTable.TableCell>

                          <MuiTable.TableCell>{venue.address}</MuiTable.TableCell>

                          <MuiTable.TableCell align="center">
                            <MuiBox display="flex" gap="10px">
                              <Tooltip title="Edit Venue" arrow placement="top" verticalOffset={-8}>
                                <SvgIconButton onClick={() => handleEdit(venue)}>
                                  <SvgIcon name="edit_circle" />
                                </SvgIconButton>
                              </Tooltip>

                              <Tooltip title="Delete Venue" arrow placement="top" verticalOffset={-8}>
                                <SvgIconButton
                                  onClick={() => {
                                    setSelectedVenue(venue);
                                    setShowDeleteOverlay(true);
                                  }}
                                >
                                  <SvgIcon name="exit_circle" />
                                </SvgIconButton>
                              </Tooltip>
                            </MuiBox>
                          </MuiTable.TableCell>
                        </MuiTable.TableRow>
                      ))}
                    </MuiTable.TableBody>
                  </MuiTable.Table>
                </MuiTable.TableContainer>
              </Box>
            </Card>
          </MuiBox>

          {/* DELETE CONFIRMATION */}
          {showDeleteOverlay && (
            <FormDialog
              open={showDeleteOverlay}
              confirmButtonAction={handleDelete}
              confirmButtonText="Delete"
              cancelButtonAction={() => {
                setSelectedVenue(null);
                setShowDeleteOverlay(false);
              }}
              cancelButtonText="Cancel"
            >
              <MuiBox>
                <Typography fontSize={24}>Are you sure you want to delete this venue?</Typography>
              </MuiBox>
            </FormDialog>
          )}

          {/* CREATE / EDIT FORM */}
          {showCreateEdit && (
            <VenueForm
              open={showCreateEdit}
              mode={selectedVenue ? 'edit' : 'create'}
              initialData={getInitialData()}
              onClose={handleOnFormClose}
              onSubmit={handleSubmit}
            />
          )}
        </MuiBox>

        {error && (
          <ActionOverlay
            title={
              <MuiBox className={classes.errorDeleteHeading}>
                <Typography className={classes.errorDeleteHeader}>{error}</Typography>
              </MuiBox>
            }
            confirmText="Close"
            onConfirm={resetError}
            width={800}
            height={350}
            appearance="error"
          />
        )}
      </FormDialog>
    </>
  );
};
